package io.horion.cv.viewModel

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import io.horion.cv.uiState.CvUiState
import io.horion.cv.api.datasource.common.PersonalInformation
import io.horion.cv.api.datasource.common.Signature
import io.horion.cv.api.datasource.common.Type
import io.horion.cv.api.datasource.cv.Activity
import io.horion.cv.api.datasource.cv.Certificate
import io.horion.cv.api.datasource.cv.Competence
import io.horion.cv.api.datasource.cv.Experience
import io.horion.cv.api.datasource.cv.Formation
import io.horion.cv.api.datasource.cv.Language
import io.horion.cv.api.datasource.cv.LanguageItem
import io.horion.cv.api.datasource.cv.Learn
import io.horion.cv.api.datasource.cv.Level
import io.horion.cv.api.datasource.cv.Model
import io.horion.cv.api.datasource.cv.OrionCV
import io.horion.cv.api.datasource.cv.Reference
import io.horion.cv.api.datasource.cv.Section
import io.horion.cv.api.datasource.cv.Stage
import io.horion.cv.api.datasource.response.Response
import io.horion.cv.api.usecase.CvUseCase
import io.horion.cv.api.usecase.PayUseCase
import io.horion.cv.api.utils.LocalStorageManager
import io.horion.cv.models.PersonalizedSectionType
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlin.time.Duration.Companion.milliseconds
import org.khronos.webgl.Int8Array
import org.w3c.files.File
import org.w3c.files.FileReader
import org.khronos.webgl.ArrayBuffer

class CvViewModel (
    private val cvUseCase: CvUseCase,
    private val scope: CoroutineScope = CoroutineScope(Dispatchers.Main)
) {

    private val json = Json { ignoreUnknownKeys = true }
    private val SESSION_KEY_DATA = "cv_form_data"
    private val SESSION_KEY_MODEL = "cv_form_model"

    var cvTitle by mutableStateOf("Cv sans titre")
        private set

    private var _cvUiState by mutableStateOf(loadFromSession())
    var cvUiState: CvUiState
        get() = _cvUiState
        private set(value) {
            _cvUiState = value
            saveToSession()
        }


    private fun loadFromSession(): CvUiState {
        return try {
            val storage = window.sessionStorage
            val dataJson = storage.getItem(SESSION_KEY_DATA)
            val modelName = storage.getItem(SESSION_KEY_MODEL)

            val data = if (dataJson != null) json.decodeFromString<OrionCV>(dataJson) else null
            val model = if (modelName != null) try { Model.valueOf(modelName) } catch (_: Exception) { null } else null
            cvTitle = data?.cvTitle ?: "Cv sans titre"
            CvUiState(
                data = data ?: OrionCV(personalInformation = PersonalInformation()),
                model = model ?: Model.SIMPLE
            )
        } catch (_: Exception) {
            CvUiState()
        }
    }

    private fun saveToSession() {
        try {
            val storage = window.sessionStorage
            storage.setItem(SESSION_KEY_DATA, json.encodeToString(OrionCV.serializer(), cvUiState.data.copy(cvTitle = cvTitle)))
            storage.setItem(SESSION_KEY_MODEL, cvUiState.model.name)
        } catch (_: Exception) { }
    }




    fun updateCvTitle(title: String) {
        cvTitle = title
    }

    var editingFormationId by mutableStateOf<String?>(null)
    var editingExperienceId by mutableStateOf<String?>(null)
    var editingCompetenceId by mutableStateOf<String?>(null)
    var editingCenterInterestId by mutableStateOf<String?>(null)
    var editingActivityId by mutableStateOf<String?>(null)
    var editingQualityId by mutableStateOf<String?>(null)
    var editingCourseId by mutableStateOf<String?>(null)
    var editingCertificatId by mutableStateOf<String?>(null)
    var editingReferenceId by mutableStateOf<String?>(null)
    var editingStageId by mutableStateOf<String?>(null)
    var editingLanguageId by mutableStateOf<String?>(null)

    var showValidationWarning by mutableStateOf(false)
        private set

    var missingFieldIds by mutableStateOf<List<String>>(emptyList())
        private set

    fun validateRequiredFields(): Boolean {
        val pi = cvUiState.data.personalInformation
        val missing = mutableListOf<String>()

        if (pi.lastName.isBlank()) missing.add("lastName")
        if (pi.firstName.isBlank()) missing.add("firstName")
        if (pi.bp.isBlank()) missing.add("bp")
        if (pi.email.isBlank()) missing.add("email")
        if (pi.phone.isBlank()) missing.add("phone")
        if (pi.city.isBlank()) missing.add("city")

        missingFieldIds = missing
        showValidationWarning = missing.isNotEmpty()
        return missing.isEmpty()
    }

    fun dismissValidationWarning() {
        showValidationWarning = false
    }

    fun createCv() {
        var result : Response?
        scope.launch {
            cvUiState = cvUiState.copy(
                loading = true,
                cvCreateSuccess = false,
                error = null,
                success = null
            )
            updateDestination()
            try {
                if(cvUiState.images != null) {
                    result = cvUseCase.uploadImage(cvUiState.images)
                    if(result?.success == true){
                        updatePersonalInfo { it.copy(photo = result!!.message) }
                    }
                }
                val title = if (cvTitle == "Cv sans titre") "" else cvTitle
                val response = cvUseCase.createCv(
                    model = cvUiState.model,
                    cv = cvUiState.data.copy(cvTitle = title)
                )

                when {
                    response == null -> {
                        cvUiState = cvUiState.copy(
                            isConnected = false,
                            loading = false,
                            error = "Impossible de contacter le serveur. Vérifiez votre connexion et réessayez."
                        )
                    }
                    response.success -> {
                        LocalStorageManager.saveSessionUuid(response.message)
                        cvUiState = cvUiState.copy(
                            cvCreateSuccess = true,
                            loading = false,
                            success = response.message,
                            error = null
                        )
                    }
                    else -> {
                        cvUiState = cvUiState.copy(
                            loading = false,
                            error = response.message
                        )
                    }
                }
            } catch (e: Exception) {
                cvUiState = cvUiState.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun insertCv(){
        scope.launch {
            cvUiState = cvUiState.copy(
                loading = true,
                error = null,
                success = null
            )
            try {
                val response = cvUseCase.saveCv(
                    cv = cvUiState.data
                )
                if (response == null){
                    cvUiState = cvUiState.copy(
                        isConnected = false,
                        loading = false
                    )
                } else if (response.success){
                    cvUiState = cvUiState.copy(
                        loading = false,
                        success = response.message
                    )
                }else{
                    cvUiState = cvUiState.copy(
                        loading = false,
                        error = response.message
                    )
                }
            }catch (e : Exception){
                cvUiState = cvUiState.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun clear(){
        cvUiState = cvUiState.copy(
            cvCreateSuccess = false
        )
    }

    fun loadCvForEdit(cvData: OrionCV, model: Model = Model.SIMPLE, title: String = "Cv sans titre") {
        cvUiState = cvUiState.copy(data = cvData, model = model)
        cvTitle = title
    }

    fun resetToBlank() {
        cvUiState = CvUiState(
            data = OrionCV(personalInformation = PersonalInformation()),
            model = cvUiState.model
        )
        cvTitle = "Cv sans titre"
    }

    suspend fun loadCvForEditById(id: String, model: String? = null): OrionCV? {
        val cv = cvUseCase.getCvById(id) ?: return null
        val name = "${cv.personalInformation.firstName} ${cv.personalInformation.lastName}".trim()
        val selectedModel = Model.entries.firstOrNull { it.name == model } ?: Model.SIMPLE
        loadCvForEdit(cv, model = selectedModel, title = name.ifBlank { "Cv sans titre" })
        return cv
    }

    fun updateExistingCv(onSuccess: () -> Unit = {}) {
        scope.launch {
            cvUiState = cvUiState.copy(
                loading = true,
                cvCreateSuccess = false,
                error = null,
                success = null
            )
            updateDestination()
            try {
                if (cvUiState.images != null) {
                    val result = cvUseCase.uploadImage(cvUiState.images)
                    if (result?.success == true) {
                        updatePersonalInfo { it.copy(photo = result.message) }
                    }
                }

                val response = cvUseCase.updateCv(
                    cv = cvUiState.data,
                    model = cvUiState.model.name
                )

                when {
                    response == null -> {
                        cvUiState = cvUiState.copy(
                            isConnected = false,
                            loading = false,
                            error = "Impossible de contacter le serveur. Vérifiez votre connexion et réessayez."
                        )
                    }
                    response.success -> {
                        cvUiState = cvUiState.copy(
                            cvCreateSuccess = true,
                            loading = false,
                            success = "CV mis à jour"
                        )
                        onSuccess()
                    }
                    else -> {
                        cvUiState = cvUiState.copy(loading = false, error = response.message)
                    }
                }
            } catch (e: Exception) {
                cvUiState = cvUiState.copy(loading = false, error = e.message)
            }
        }
    }

    fun updatePhoto(image: File) {
        val reader = FileReader()
        reader.onload = { event ->
            // On récupère le résultat sous forme d'ArrayBuffer
            val arrayBuffer = event.target.asDynamic().result as ArrayBuffer
            // On convertit l'ArrayBuffer en ByteArray (Kotlin)
            val bytes = Int8Array(arrayBuffer).unsafeCast<ByteArray>()

            // On met à jour le State avec les vrais octets
            cvUiState = cvUiState.copy(images = bytes)
        }
        reader.readAsArrayBuffer(image)
    }

    fun updateModel(model: Model) {
        cvUiState = cvUiState.copy(
            model = model
        )
    }

    fun updateType(type: Type) {
        val currentData = cvUiState.data

        cvUiState = cvUiState.copy(
            data = currentData.copy(
                type = type
            )
        )
    }

    fun updateProfil(profilHtml: String) {
        val currentData = cvUiState.data

        cvUiState = cvUiState.copy(
            data = currentData.copy(
                profil = profilHtml
            )
        )
    }

    fun updatePersonalInfo(update : (PersonalInformation) -> PersonalInformation) {
        val currentData = cvUiState.data

        val currentPersonalInfo =
            currentData.personalInformation

        cvUiState = cvUiState.copy(
            data = currentData.copy(
                personalInformation = update(currentPersonalInfo)
            )
        )
        if (showValidationWarning) {
            validateRequiredFields()
            if (missingFieldIds.isEmpty()) {
                showValidationWarning = false
            }
        }
    }

    fun updateFormation(index: Int, formation: Formation) {
        val currentData = cvUiState.data
        val currentFormations = currentData.formations.toMutableList()

        if (index in currentFormations.indices) {
            currentFormations[index] = formation
        } else {
            currentFormations.add(formation)
        }

        cvUiState = cvUiState.copy(
            data = currentData.copy(formations = currentFormations)
        )
    }

    fun deleteFormation(index: Int) {
        val currentData = cvUiState.data
        val currentFormations = currentData.formations.toMutableList()

        if (index in currentFormations.indices) {
            currentFormations.removeAt(index)

            cvUiState = cvUiState.copy(
                data = currentData.copy(formations = currentFormations)
            )
        }
    }
    fun updateExperience(index: Int, experience: Experience) {
        val currentData = cvUiState.data
        val currentList = currentData.experiences.toMutableList()

        if (index in currentList.indices) {
            currentList[index] = experience
        } else {
            currentList.add(experience)
        }

        cvUiState = cvUiState.copy(data = currentData.copy(experiences = currentList))
    }

    fun deleteExperience(index: Int) {
        val currentData = cvUiState.data
        val currentList = currentData.experiences.toMutableList()

        if (index in currentList.indices) {
            currentList.removeAt(index)
            cvUiState = cvUiState.copy(data = currentData.copy(experiences = currentList))
        }
    }
    fun updateCompetence(index: Int, competence: Competence) {
        val currentData = cvUiState.data
        val currentList = currentData.competences.toMutableList()

        if (index in currentList.indices) {
            currentList[index] = competence
        } else {
            currentList.add(competence)
        }

        cvUiState = cvUiState.copy(data = currentData.copy(competences = currentList))
    }

    fun deleteCompetence(index: Int) {
        val currentData = cvUiState.data
        val currentList = currentData.competences.toMutableList()

        if (index in currentList.indices) {
            currentList.removeAt(index)
            cvUiState = cvUiState.copy(data = currentData.copy(competences = currentList))
        }
    }

    fun updateHobby(index: Int, hobby: String) {
        val currentData = cvUiState.data
        val currentList = currentData.hobbies.toMutableList()

        if (index in currentList.indices) {
            currentList[index] = hobby
        } else {
            currentList.add(hobby)
        }

        cvUiState = cvUiState.copy(data = currentData.copy(hobbies = currentList))
    }

    fun deleteHobby(index: Int) {
        val currentData = cvUiState.data
        val currentList = currentData.hobbies.toMutableList()

        if (index in currentList.indices) {
            currentList.removeAt(index)
            cvUiState = cvUiState.copy(data = currentData.copy(hobbies = currentList))
        }
    }

    fun updateActivity(index: Int, activity: Activity) {
        val currentData = cvUiState.data
        val currentList = currentData.extracurricularActivities.toMutableList()

        if (index in currentList.indices) {
            currentList[index] = activity
        } else {
            currentList.add(activity)
        }

        cvUiState = cvUiState.copy(data = currentData.copy(extracurricularActivities = currentList))
    }

    fun deleteActivity(index: Int) {
        val currentData = cvUiState.data
        val currentList = currentData.extracurricularActivities.toMutableList()

        if (index in currentList.indices) {
            currentList.removeAt(index)
            cvUiState = cvUiState.copy(data = currentData.copy(extracurricularActivities = currentList))
        }
    }

    fun updateQuality(index: Int, quality: String) {
        val currentData = cvUiState.data
        val currentList = currentData.qualities.toMutableList()

        if (index in currentList.indices) {
            currentList[index] = quality
        } else {
            currentList.add(quality)
        }

        cvUiState = cvUiState.copy(data = currentData.copy(qualities = currentList))
    }

    fun deleteQuality(index: Int) {
        val currentData = cvUiState.data
        val currentList = currentData.qualities.toMutableList()

        if (index in currentList.indices) {
            currentList.removeAt(index)
            cvUiState = cvUiState.copy(data = currentData.copy(qualities = currentList))
        }
    }

    fun updateCourse(index: Int, learn: Learn) {
        val currentData = cvUiState.data
        val currentList = currentData.learns.toMutableList()

        if (index in currentList.indices) {
            currentList[index] = learn
        } else {
            currentList.add(learn)
        }

        cvUiState = cvUiState.copy(data = currentData.copy(learns = currentList))
    }

    fun deleteCourse(index: Int) {
        val currentData = cvUiState.data
        val currentList = currentData.learns.toMutableList()

        if (index in currentList.indices) {
            currentList.removeAt(index)
            cvUiState = cvUiState.copy(data = currentData.copy(learns = currentList))
        }
    }

    fun updateCertificate(index: Int, cert: Certificate) {
        val currentData = cvUiState.data
        val currentList = currentData.certificates.toMutableList()

        if (index in currentList.indices) {
            currentList[index] = cert
        } else {
            currentList.add(cert)
        }

        cvUiState = cvUiState.copy(data = currentData.copy(certificates = currentList))
    }

    fun deleteCertificate(index: Int) {
        val currentData = cvUiState.data
        val currentList = currentData.certificates.toMutableList()

        if (index in currentList.indices) {
            currentList.removeAt(index)
            cvUiState = cvUiState.copy(data = currentData.copy(certificates = currentList))
        }
    }

    fun updateReference(index: Int, ref: Reference) {
        val currentData = cvUiState.data
        val currentList = currentData.references.toMutableList()

        if (index in currentList.indices) {
            currentList[index] = ref
        } else {
            currentList.add(ref)
        }

        cvUiState = cvUiState.copy(data = currentData.copy(references = currentList))
    }

    fun deleteReference(index: Int) {
        val currentData = cvUiState.data
        val currentList = currentData.references.toMutableList()

        if (index in currentList.indices) {
            currentList.removeAt(index)
            cvUiState = cvUiState.copy(data = currentData.copy(references = currentList))
        }
    }
    fun updateStage(index: Int, stage: Stage) {
        val currentData = cvUiState.data
        val currentList = currentData.stages.toMutableList()

        if (index in currentList.indices) {
            currentList[index] = stage
        } else {
            currentList.add(stage)
        }

        cvUiState = cvUiState.copy(data = currentData.copy(stages = currentList))
    }

    fun deleteStage(index: Int) {
        val currentData = cvUiState.data
        val currentList = currentData.stages.toMutableList()

        if (index in currentList.indices) {
            currentList.removeAt(index)
            cvUiState = cvUiState.copy(data = currentData.copy(stages = currentList))
        }
    }

    fun updateAchievements(text: String) {
        val currentData = cvUiState.data
        cvUiState = cvUiState.copy(
            data = currentData.copy(achievements = listOf(text))
        )
    }

    fun updateSignature(city: String? = null, date: String? = null, image: String? = null) {
        val currentData = cvUiState.data
        val currentSignature = currentData.signature ?: Signature("", "", "")

        val updatedSignature = currentSignature.copy(
            city = city ?: currentSignature.city,
            date = date ?: currentSignature.date,
            image = image ?: currentSignature.image
        )

        cvUiState = cvUiState.copy(
            data = currentData.copy(signature = updatedSignature)
        )
    }

    fun updateLanguage(index: Int, name: String? = null, level: String? = null) {
        val currentData = cvUiState.data
        val currentList = currentData.languages.toMutableList()

        val existingItem = currentList.getOrNull(index) ?: LanguageItem(Language.FRENCH, Level.BEGINNER)

        val newLanguage = if (name != null) {
            try { Language.valueOf(name.uppercase()) } catch (_: Exception) { existingItem.language }
        } else existingItem.language

        val newLevel = if (level != null) {
            try { Level.valueOf(level.uppercase()) } catch (_: Exception) { existingItem.level }
        } else existingItem.level

        val updatedItem = LanguageItem(language = newLanguage, level = newLevel)

        if (index in currentList.indices) {
            currentList[index] = updatedItem
        } else {
            currentList.add(updatedItem)
        }

        cvUiState = cvUiState.copy(data = currentData.copy(languages = currentList))
    }

    fun deleteLanguage(index: Int) {
        val currentData = cvUiState.data
        val currentList = currentData.languages.toMutableList()

        if (index in currentList.indices) {
            currentList.removeAt(index)

            cvUiState = cvUiState.copy(
                data = currentData.copy(languages = currentList)
            )
        }
    }
    fun updateDestination(){
        val currentData = cvUiState.data
        cvUiState = cvUiState.copy(
            data = currentData.copy(
                personalInformation = cvUiState.data.personalInformation.copy(
                    state = " ",
                    country = " "
                )
            ),
        )
    }
    fun addPersonalizedSection(section: Section) {
        val currentData = cvUiState.data

        cvUiState = cvUiState.copy(
            data = currentData.copy(
                personalizedSections =
                    currentData.personalizedSections + section
            )
        )
    }

    fun updatePersonalizedSection(
        sectionId: String,
        update: (Section) -> Section
    ) {
        val currentData = cvUiState.data

        val updatedSections = currentData.personalizedSections.map { section ->
            if (section.id == sectionId) {
                update(section)
            } else {
                section
            }
        }

        cvUiState = cvUiState.copy(
            data = currentData.copy(
                personalizedSections = updatedSections
            )
        )
    }

    fun removePersonalizedSection(sectionId: String) {
        val currentData = cvUiState.data

        cvUiState = cvUiState.copy(
            data = currentData.copy(
                personalizedSections =
                    currentData.personalizedSections.filterNot {
                        it.id == sectionId
                    }
            )
        )
    }

    fun addEmptyPersonalizedEntry(entryId: String) {
        val currentData = cvUiState.data

        val newSection = Section(
            id = entryId,
            type = PersonalizedSectionType.ENTRIES,
            title = "",
            resume = "",
            started = "",
            ended = "",
            description = ""
        )

        cvUiState = cvUiState.copy(
            data = currentData.copy(
                personalizedSections =
                    currentData.personalizedSections + newSection
            )
        )
    }
}